import React, { useContext, useState } from 'react';
import { Link, generatePath, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, Breadcrumbs, Typography, Link as MuiLink } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import Bluebird from 'bluebird';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import useActiveSite from '../../../hooks/useActiveSite';
import ToDoAlertCard from './ToDoAlertCard';
import { receiveRemoveSiteTodo, removeSiteTodo } from '../../../actions/sites';
import snackbarHelper from '../../../helpers/snackbarHelper';
import OverlayLoader from '../../../components/OverlayLoader';

export const FIRST_RESCUE = 'First Rescue';

const containerSx = {
  width: '100%',
  height: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  paddingLeft: 2,
  paddingRight: 2,
};

const SDToDoToDoFirstRescueBreadcrumbs = () => (
  <Box boxShadow={0}>
    <Breadcrumbs aria-label="breadcrumb">
      <StyledRescuerBreadcrumb
        component={Link}
        to={generatePath(routes.mobileDashboard)}
        label="Home"
        icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
      />
      <StyledRescuerTextBreadcrumb component={Link} to={routes.mobileToDo} label="To-do" />
      <StyledRescuerTextBreadcrumb component="a" href="#" label="First Rescue" />
    </Breadcrumbs>
  </Box>
);

const ToDoFirstRescue = () => {
  const location = useLocation();
  const activeSite = useActiveSite();
  const dispatch = useDispatch();
  const [currentCardIndex, setCurrentCardIndex] = useState(location.state?.toDoCardIndex || 0);
  const [loading, setLoading] = useState();
  const sdTodosEntities = useSelector((state) => state.entities.sites.sdTodos);
  const sdTodos = sdTodosEntities.bySiteId[activeSite.id];
  const firstRescues = sdTodos?.firstRescues || [];

  const handleSkipClick = () => {
    setCurrentCardIndex(currentCardIndex + 1);
  };

  const handleRemoveClick = (rescue, handleCardOut, animationTime) => {
    setLoading(true);
    Bluebird.try(() =>
      dispatch(removeSiteTodo(activeSite.id, rescue.entity_id, rescue.entity_type, rescue.type, false))
    )
      .then(() => {
        handleCardOut();
        // Wait for animation to complete before removing todo from the store
        setTimeout(() => {
          dispatch(receiveRemoveSiteTodo(activeSite.id, rescue.entity_id, rescue.type));
        }, animationTime);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  // Show if there is no more pickups to review or when the last card has been skipped
  if (!firstRescues.length || currentCardIndex + 1 > firstRescues.length) {
    return (
      <>
        <SDToDoToDoFirstRescueBreadcrumbs />
        <Box sx={containerSx}>
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              WebkitTransform: 'translate(-50%, -50%)',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography color="textSecondary" variant="h5">
              You’re all caught up, great work!
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <OverlayLoader loading={loading}>
      <SDToDoToDoFirstRescueBreadcrumbs />

      <Box sx={containerSx}>
        <Box
          sx={{
            position: 'relative',
            height: '100%',
          }}
        >
          {firstRescues.map((rescue, index) => (
            <ToDoAlertCard
              key={rescue.entity_id}
              rescue={rescue}
              type={FIRST_RESCUE}
              current={index === currentCardIndex}
              next={firstRescues.length <= index ? null : index === currentCardIndex + 1}
              onRemoveClick={handleRemoveClick}
              onSkipClick={handleSkipClick}
              onReviewClick={handleRemoveClick}
              reviewLabel="Done"
              notReviewable
              content={
                <span>
                  <b>{rescue?.details?.claimer}</b> has just claimed their first rescue, picking up from{' '}
                  <b>{rescue?.details?.location}</b> on <b>{moment(rescue?.details?.date).format('ddd MMM Do')}</b>. You
                  may want to email them at{' '}
                  <u>
                    <MuiLink href={`mailto:${rescue?.details?.claimer_email}`} underline="hover">
                      {rescue?.details?.claimer_email}
                    </MuiLink>
                  </u>{' '}
                  to make sure they&apos;re ready to go!
                </span>
              }
            />
          ))}
        </Box>
      </Box>
    </OverlayLoader>
  );
};

export default ToDoFirstRescue;
